import React, { useState } from 'react';
import { Avatar, Dropdown, Button, Typography, Badge } from 'antd';
import { LogoutOutlined, SettingOutlined, KeyOutlined, DownOutlined } from '@ant-design/icons';
import { SubscriptionsOutlined, DataUsageOutlined } from '@mui/icons-material';
import type { MenuProps } from 'antd';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { getLoggedInUser } from '@/store/account/selector';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import LocalStorageUtility from '@/utils/localStorage';
import { resetTranslationCookies } from '@/utils/translation';

const { Text } = Typography;

export interface FloatingAccountBubbleProps {
	className?: string;
	debrand?: boolean;
}

const FloatingAccountBubble: React.FC<FloatingAccountBubbleProps> = ({ className, debrand }) => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const dispatch = useAppDispatch();
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const getInitials = (name?: string) => {
		if (!name) {
			return 'U';
		}
		return name
			.split(' ')
			.map((word) => word.charAt(0))
			.join('')
			.toUpperCase()
			.slice(0, 2);
	};

	const handleLogout = () => {
		LocalStorageUtility.clearLocalData();
		resetTranslationCookies();

		if (window.churnkey) {
			window.churnkey.clearState?.();
		}
	};

	const handleMenuClick = (key: string) => {
		setDropdownOpen(false);

		switch (key) {
			case 'settings':
				dispatch(setCurrentPage(ReceptionistDashboardSiderItem.SETTINGS));
				break;
			case 'password':
				dispatch(setCurrentPage(ReceptionistDashboardSiderItem.PASSWORD));
				break;
			case 'subscription':
				dispatch(setCurrentPage(ReceptionistDashboardSiderItem.SUBSCRIPTION));
				break;
			case 'usage':
				dispatch(setCurrentPage(ReceptionistDashboardSiderItem.CREDITS));
				break;
			case 'logout':
				handleLogout();
				break;
		}
	};

	const buildMenuItems = (): MenuProps['items'] => {
		const items: MenuProps['items'] = [
			{
				key: 'settings',
				label: (
					<div className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-slate-600">
						<SettingOutlined className="text-base text-slate-500" />
						<span>Account Settings</span>
					</div>
				),
				onClick: () => handleMenuClick('settings'),
			},
			{
				key: 'password',
				label: (
					<div className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-slate-600">
						<KeyOutlined className="text-base text-slate-500" />
						<span>Change Password</span>
					</div>
				),
				onClick: () => handleMenuClick('password'),
			},
		];

		if (loggedInUser?.userType !== 'SubAccount' && !debrand) {
			items.push(
				{
					key: 'subscription',
					label: (
						<div className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-slate-600">
							<SubscriptionsOutlined className="text-base text-slate-500" />
							<span>Subscription</span>
						</div>
					),
					onClick: () => handleMenuClick('subscription'),
				},
				{
					key: 'usage',
					label: (
						<div className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-slate-600">
							<DataUsageOutlined className="text-base text-slate-500" />
							<span>Usage & Billing</span>
						</div>
					),
					onClick: () => handleMenuClick('usage'),
				},
			);
		}

		items.push(
			{
				type: 'divider',
			},
			{
				key: 'logout',
				label: (
					<div className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-red-500">
						<LogoutOutlined className="text-base text-red-500" />
						<span>Log Out</span>
					</div>
				),
				onClick: () => handleMenuClick('logout'),
			},
		);

		return items;
	};

	return (
		<div className={`floating-account-bubble ${className || ''}`}>
			<Dropdown
				menu={{
					items: buildMenuItems(),
					className: 'rounded-xl p-1.5 min-w-[200px] bg-white font-sans',
				}}
				trigger={['click']}
				placement="bottomRight"
				open={dropdownOpen}
				onOpenChange={setDropdownOpen}
				overlayClassName="floating-account-dropdown"
				overlayStyle={{
					borderRadius: '12px',
					boxShadow:
						'0 4px 20px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
				}}
				arrow={false}
				getPopupContainer={() => document.body}
			>
				<Button type="text" className="p-0 hover:bg-transparent">
					<div className="flex items-center gap-3">
						<div className="relative">
							<Badge dot status="success" offset={[-6, 6]} className="status-indicator">
								<Avatar
									size={44}
									className="font-semibold text-base border-2 border-white/80"
									style={{
										backgroundColor: loggedInUser?.avatarBg || '#3b82f6',
									}}
								>
									{getInitials(loggedInUser?.businessName)}
								</Avatar>
							</Badge>
						</div>

						<div className="flex flex-col">
							<Text className="text-sm font-medium text-gray-900">
								{loggedInUser?.businessName || 'Business Name'}
							</Text>
							<Text className="text-xs text-gray-500">{loggedInUser?.username || 'username'}</Text>
						</div>

						<DownOutlined className="text-gray-400" />
					</div>
				</Button>
			</Dropdown>
		</div>
	);
};

export default FloatingAccountBubble;
